import { Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useBadges } from "api/badges";
import { getBackendErrors } from "api/errors";
import { FormButtons } from "components/FormControls/FormButtons";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { OLModal } from "components/Tooltips/OLModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { ExternalLink } from "ExternalLink";
import { Form, Formik, FormikHelpers } from "formik";
import { useMessage } from "hooks/intl";
import { backendErrors } from "utilities/backendErrors";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    description: {
      fontSize: 14,
      lineHeight: "20px",
      color: theme.gray800,
      marginBottom: theme.spacing(12),
    },
    link: { fontSize: 14, lineHeight: "20px", textDecoration: "underline" },
    btnWrapper: {
      justifyContent: "space-between",
      margin: theme.spacing(20, 0, 0),

      "& button": {
        width: "100%",
      },
    },
  }),
);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  badgeTypeId?: string;
}

type FormValues = { name: string };

export const BadgeNameChanger = ({ isOpen, onClose, badgeTypeId }: Props) => {
  const { message } = useMessage();
  const classes = useStyles();
  const { notify } = useNotifications();
  const { useBadgeDetails, updateBadge } = useBadges();
  const { data: badge } = useBadgeDetails(badgeTypeId);

  const onSubmit = (
    values: FormValues,
    { setErrors }: FormikHelpers<FormValues>,
  ) => {
    return updateBadge({ id: badgeTypeId, name: values.name })
      .then(() => {
        notify(message({ id: "badgeNameUpdated" }));
        onClose();
      })
      .catch((exception) => {
        const errors = backendErrors(getBackendErrors(exception));
        setErrors(errors);
      });
  };

  return (
    <OLModal
      onClose={onClose}
      open={isOpen}
      header={message({ id: "changeBadgeName" })}
      modalWidth={536}
    >
      <Formik
        initialValues={{ name: badge?.name || "" }}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ handleSubmit, isSubmitting, errors }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <Typography className={classes.description}>
                {message(
                  { id: "changeBadgeNameDesc" },
                  {
                    learnMore: (
                      <ExternalLink
                        className={classes.link}
                        path="https://help.openloyalty.io/main-features/achievements/badges"
                      >
                        {message({
                          id: "learnMoreAboutBadges",
                        })}
                      </ExternalLink>
                    ),
                  },
                )}
              </Typography>
              <TextInput
                name="name"
                width="100%"
                label={
                  <RequiredFieldLabel text={message({ id: "newBadgeName" })} />
                }
              />

              <FormButtons
                className={classes.btnWrapper}
                onCancelClick={onClose}
                onSubmitClick={() => handleSubmit()}
                submitMessage={message({ id: "saveChanges" })}
                cancelMessage={message({ id: "cancel" })}
                disableSubmit={isSubmitting}
              />
            </Form>
          );
        }}
      </Formik>
    </OLModal>
  );
};
